import { Component } from '@theme/component';
import { DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback } from '@theme/utilities';

const RAIL_QUERY = '(min-width: 990px)';
const FILTER_TOGGLE = '[on\\:click="#filters-drawer/showDialog"]';
const BODY = ':scope > .ca-facets-body';
const RESPONSE_BODY = '.ca-facets-body';

/** Holds the single copy of the facet filters: in the rail from 990px up, in the filter drawer below. */
export class CaFacetsSlot extends Component {
  static reload = () => window.location.reload();

  #query = window.matchMedia(RAIL_QUERY);
  #sequence = 0;
  /** @type {HTMLElement | null} */
  #drawer = null;
  /** @type {HTMLFormElement | null} */
  #form = null;

  connectedCallback() {
    super.connectedCallback();

    this.#drawer = document.getElementById('filters-drawer');
    this.#form = this.closest('form');

    this.#query.addEventListener('change', this.#place);
    window.addEventListener('click', this.#onToggleClick, true);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
    this.#drawer?.addEventListener(DialogCloseEvent.eventName, this.#place);
    this.#form?.addEventListener('formdata', this.#onFormData);
    this.addEventListener('focusout', this.#onFocusOut);

    if (!this.#query.matches) requestIdleCallback(this.#place);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#query.removeEventListener('change', this.#place);
    window.removeEventListener('click', this.#onToggleClick, true);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
    this.#drawer?.removeEventListener(DialogCloseEvent.eventName, this.#place);
    this.#form?.removeEventListener('formdata', this.#onFormData);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.#sequence++;
  }

  /** @returns {HTMLElement | null} */
  get #drawerSlot() {
    return this.#drawer?.querySelector('[data-ca-facets-slot="drawer"]') ?? null;
  }

  /** @returns {HTMLElement | null} */
  get #body() {
    return this.querySelector(BODY) ?? this.#drawerSlot?.querySelector(BODY) ?? null;
  }

  #place = () => {
    const body = this.#body;
    const slot = this.#drawerSlot;
    if (!body || !slot) return;

    const target = this.#query.matches ? this : slot;
    if (body.parentElement !== target && !this.#isPinned(body, slot)) target.append(body);
  };

  /**
   * @param {HTMLElement} body
   * @param {HTMLElement} slot
   */
  #isPinned(body, slot) {
    const dialog = this.#drawer?.querySelector('dialog');
    const leavingOpenDrawer = body.parentElement === slot && dialog instanceof HTMLDialogElement && dialog.open;
    return leavingOpenDrawer || body.contains(document.activeElement);
  }

  /** @param {MouseEvent} event */
  #onToggleClick = (event) => {
    if (this.#query.matches) return;
    if (event.target instanceof Element && event.target.closest(FILTER_TOGGLE)) this.#place();
  };

  #onFocusOut = () => {
    if (!this.#query.matches) setTimeout(this.#place, 0);
  };

  /** @param {Event} event */
  #onFormData = (event) => {
    const body = this.#body;
    const formData = /** @type {FormDataEvent} */ (event).formData;
    if (!body || this.contains(body) || !formData) return;

    for (const field of body.querySelectorAll('input[name], select[name]')) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement) || field.disabled) continue;
      if (field instanceof HTMLInputElement && /^(checkbox|radio)$/.test(field.type) && !field.checked) continue;
      formData.append(field.name, field.value);
    }
  };

  #onFilterUpdate = () => {
    this.#refresh(++this.#sequence);
  };

  /** @param {number} sequence */
  async #refresh(sequence) {
    try {
      const sectionId = this.closest('.shopify-section')?.id;
      if (!sectionId) throw new Error('facet slot is outside a section');

      const html = await sectionRenderer.getSectionHTML(sectionId, !window.Shopify?.designMode);
      if (sequence !== this.#sequence) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector(RESPONSE_BODY);
      if (!next && !doc.querySelector('ca-facets-slot, [data-ca-facets-slot]')) return;
      const body = this.#body;
      if (!next || !body) throw new Error('facet filters missing from the section response');

      morph(body, next, MORPH_OPTIONS);
    } catch {
      if (sequence === this.#sequence) CaFacetsSlot.reload();
    }
  }
}

if (!customElements.get('ca-facets-slot')) customElements.define('ca-facets-slot', CaFacetsSlot);
